import { useMemo } from 'react'
import useSWR from 'swr'

import { apiFetcher } from '@/data/fetcher'
import { Invoice } from '@/interfaces/Invoice'
import { components } from '@invo.space/shared'

export type BankAccountTransaction = components['schemas']['BankAccountTransaction']
export type BankAccount = components['schemas']['BankAccount']

export type DocType = 'invoice' | 'order' | 'expense'

export interface MatchedDoc {
  type: DocType
  doc: Invoice
}

export const docTypeLabel: Record<DocType, string> = {
  invoice: 'Faktura',
  order: 'Výzva k platbě',
  expense: 'Náklad',
}

/** Banky, které umíme propojit. Kód banky slouží k odkazu na obnovení propojení. */
export const bankProviders = [
  { slug: 'fio', name: 'Fio banka', code: '2010', method: 'API token z internetového bankovnictví' },
  { slug: 'moneta', name: 'Moneta', code: '0600', method: 'API token z internetového bankovnictví' },
  { slug: 'raiffeisen', name: 'Raiffeisenbank', code: '5500', method: 'Certifikát a heslo, jen firemní účty' },
  { slug: 'erste', name: 'Česká spořitelna', code: '0800', method: 'Přihlášení přes internetové bankovnictví' },
] as const

export const providerForBankCode = (code: string | undefined) => bankProviders.find((p) => p.code === code)

export const txAmount = (t: BankAccountTransaction) => Number(t.amount?.amount ?? 0)

/** Hlavní text řádku: u příchozích plátce, u odchozích komentář nebo zpráva. */
export const txTitle = (t: BankAccountTransaction) => {
  const incoming = txAmount(t) > 0
  return (incoming ? t.payer || t.comment || t.message : t.comment || t.message || t.payer) || (incoming ? 'Příchozí platba' : 'Odchozí platba')
}

export const txSubtitle = (t: BankAccountTransaction) => {
  const title = txTitle(t)
  return [t.message, t.comment].find((v) => v && v !== title) ?? null
}

export const formatTxDate = (date: string | undefined | null) =>
  date ? new Intl.DateTimeFormat('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' }).format(new Date(date)) : '-'

/**
 * Doklady pro párování. Backend páruje příchozí platby podle VS nebo čísla faktury či výzvy,
 * stav párování ale v API transakcí nevrací, proto ho odvozujeme stejným pravidlem tady.
 */
export const useMatchIndex = (years: number[]) => {
  const key = years.length ? ['bank-match-docs', ...years] : null
  const { data, isLoading } = useSWR(key, () =>
    Promise.all(
      years.flatMap((y) =>
        (['invoices', 'orders', 'expenses'] as const).map((api) =>
          apiFetcher(`/api/v2/${api}?year=${y}&limit=1000`)
            .then((docs: Invoice[]) => ({ api, docs: Array.isArray(docs) ? docs : [] }))
            .catch(() => ({ api, docs: [] as Invoice[] })),
        ),
      ),
    ),
  )

  const index = useMemo(() => {
    const incoming = new Map<string, MatchedDoc>()
    const outgoing = new Map<string, MatchedDoc>()
    for (const { api, docs } of data ?? []) {
      const type: DocType = api === 'invoices' ? 'invoice' : api === 'orders' ? 'order' : 'expense'
      const target = type === 'expense' ? outgoing : incoming
      for (const doc of docs) {
        for (const k of [doc.vs, doc.symbol]) {
          if (k && !target.has(k)) target.set(k, { type, doc })
        }
      }
    }
    return { incoming, outgoing }
  }, [data])

  return { index, isLoading }
}

export const matchTransaction = (t: BankAccountTransaction, index: ReturnType<typeof useMatchIndex>['index']): MatchedDoc | null => {
  if (!t.vs) return null
  const vs = t.vs.replace(/^0+/, '') || t.vs
  const map = txAmount(t) > 0 ? index.incoming : index.outgoing
  return map.get(t.vs) ?? map.get(vs) ?? null
}

export const docPath = (m: MatchedDoc) => `/${m.type}/${m.doc.symbol}`
